/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  OrderPaymentType,
  OrderStatus,
} from "../../constants/misc"

import I18n from "i18n-js"
import { shouldShowGKMMError } from "../../utils/misc"
import { timestampToLocalDateTime } from "../../utils/datetime"
import { 
  GKMM_DEPLOY_TIMESTAMP_ML,
  GKMM_DEPLOY_TZ,
  GKMM_DEPLOY_FORMAT,
} from "../../constants/misc"

interface OrderInit {
  createdTime: Timestamp
  updatedTime: Timestamp
  storeID: StoreID
  longOrderID: LongOrderID
  shortOrderID: ShortOrderID
  bookingID: BookingID
  orderType: string
  amount: PriceInMin
  orderValue: PriceInMin
  gfCommission: PriceInMin
  mexDiscount: PriceInMin
  withholdingTax: PriceInMin
  earnings: PriceInMin
  tax: PriceInMin
  adjustments: PriceInMin
  deliveryFee: PriceInMin
  payMethod: string
  status: string
  txnIDs: Array<TransactionID>
  gkCommission: PriceInMin
  stepUpGFCommission: PriceInMin
  stepUpGKCommission: PriceInMin
  merchantCharge: PriceInMin
  serviceCharge: PriceInMin
  channelType: string
  channelCommission: PriceInMin
  deliveryCommission: PriceInMin
  totalCommission: PriceInMin
  mexDeliveryCharge: PriceInMin
  additionalInfo: string
  deliveryType: string
}

export class Order {
  constructor({
    createdTime,
    updatedTime,
    storeID,
    longOrderID,
    shortOrderID,
    bookingID,
    orderType,
    amount,
    orderValue,
    gfCommission,
    mexDiscount,
    withholdingTax,
    earnings,
    tax,
    adjustments,
    deliveryFee,
    payMethod,
    status,
    txnIDs,
    gkCommission,
    stepUpGFCommission,
    stepUpGKCommission,
    merchantCharge,
    serviceCharge,
    channelType,
    channelCommission,
    deliveryCommission,
    mexDeliveryCharge,
    deliveryType,
  }: OrderInit) {
    this.createdTime = createdTime
    this.updatedTime = updatedTime
    this.storeID = storeID
    this.longOrderID = longOrderID
    this.shortOrderID = shortOrderID
    this.bookingID = bookingID
    this.orderType = orderType
    this.amount = amount
    this.orderValue = orderValue
    this.gfCommission = gfCommission
    this.mexDiscount = mexDiscount
    this.withholdingTax = withholdingTax
    this.earnings = earnings
    this.tax = tax
    this.adjustments = adjustments
    this.deliveryFee = deliveryFee
    this.payMethod = payMethod
    this.status = status
    this.txnIDs = txnIDs
    this.gkCommission = gkCommission
    this.finalOrderValue = this.isPaid
      ? this.orderValue
      : 0
    this.stepUpGFCommission = stepUpGFCommission
    this.stepUpGKCommission = stepUpGKCommission
    this.merchantCharge = this.isPaid ? merchantCharge : 0
    this.serviceCharge = this.isPaid ? serviceCharge : 0
    this.channelType = channelType
    this.channelCommission = channelCommission
    this.deliveryCommission = deliveryCommission
    this.totalCommission = this
      .isFeeStructureUnMigratedOrder
      ? gfCommission
      : channelCommission + deliveryCommission
    this.mexDeliveryCharge = mexDeliveryCharge
    this.additionalInfo = this.orderErrorMessage()
    this.deliveryType = deliveryType
  }

  orderErrorMessage(): string {
    const orderUpdatetime = new Date(
      this.updatedTime,
    ).getTime()
    if (shouldShowGKMMError(this.shortOrderID, orderUpdatetime)) {
      return I18n.t("merchant.tabs.order.table.error.invalidGkmm", { // TODO: refactor or clean up - avoid I18n in models
        date: timestampToLocalDateTime(
          GKMM_DEPLOY_TIMESTAMP_ML,
          GKMM_DEPLOY_TZ,
          GKMM_DEPLOY_FORMAT,
        ),
      })
    }
    return ""
  }

  get hasLinkedTransactions(): boolean {
    return this.txnIDs.length !== 0
  }

  get isPaid(): boolean {
    return (
      this.status === OrderStatus.complete ||
      this.orderType === OrderPaymentType.autoPaid
    )
  }

  get isFeeStructureUnMigratedOrder(): boolean {
    return (
      this.gfCommission !== 0 &&
      this.channelCommission === 0 &&
      this.deliveryCommission === 0
    )
  }

  public readonly createdTime: Timestamp

  public readonly updatedTime: Timestamp

  public readonly storeID: StoreID

  public readonly longOrderID: LongOrderID

  public readonly shortOrderID: ShortOrderID

  public readonly bookingID: BookingID

  public readonly orderType: string

  public readonly amount: PriceInMin

  public readonly orderValue: PriceInMin

  public readonly gfCommission: PriceInMin

  public readonly gkCommission: PriceInMin

  public readonly mexDiscount: PriceInMin

  public readonly withholdingTax: PriceInMin

  public readonly earnings: PriceInMin

  public readonly tax: PriceInMin

  public readonly adjustments: PriceInMin

  public readonly deliveryFee: PriceInMin

  public readonly payMethod: string

  public readonly status: string

  public readonly txnIDs: Array<TransactionID>

  public readonly finalOrderValue: PriceInMin

  public readonly stepUpGFCommission: PriceInMin

  public readonly stepUpGKCommission: PriceInMin

  public readonly merchantCharge: PriceInMin

  public readonly serviceCharge: PriceInMin

  public readonly channelType: string

  public readonly channelCommission: PriceInMin

  public readonly deliveryCommission: PriceInMin

  public readonly totalCommission: PriceInMin

  public readonly mexDeliveryCharge: PriceInMin

  public readonly additionalInfo: string

  public readonly deliveryType: string
}
